import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Broadcast, Plus, Copy, Trash, Lightning, ArrowClockwise, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const SISTER_SITES = [
  "kerala.com", "indias.com", "indiatourism.pro", "keralatourism.pro",
  "usapulse.net", "farmerspulse.com", "naturepulse.net",
];

function StatusPill({ ok, code }) {
  if (ok == null) return <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-500"><Clock size={11} weight="bold" />No deliveries yet</span>;
  return ok
    ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><CheckCircle size={12} weight="fill" />OK {code}</span>
    : <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700"><XCircle size={12} weight="fill" />Fail {code || "—"}</span>;
}

export default function WebhookConsole() {
  const [hooks, setHooks] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [url, setUrl] = useState("");
  const [site, setSite] = useState("");
  const [creating, setCreating] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  const [testing, setTesting] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/webhooks").then((r) => setHooks(r.data)).catch(() => {});
    api.get("/admin/webhook-deliveries", { params: { limit: 50 } }).then((r) => setDeliveries(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!url.startsWith("https://")) { toast.error("Webhook URL must start with https://"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/webhooks", { url, site: site || null });
      setNewSecret({ secret: data.secret, url: data.url });
      setUrl(""); setSite("");
      load();
      toast.success("Webhook registered");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not register webhook");
    }
    setCreating(false);
  };

  const remove = async (id) => {
    await api.delete(`/admin/webhooks/${id}`);
    load(); toast.success("Webhook deactivated");
  };
  const toggle = async (id, active) => {
    await api.post(`/admin/webhooks/${id}/activate`, null, { params: { active } });
    load();
  };
  const test = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post(`/admin/webhooks/${id}/test`);
      if (data.ok) toast.success(`Test delivered · ${data.status_code}`);
      else toast.error(`Test failed · ${data.status_code || data.error || "no response"}`);
    } catch { toast.error("Test failed"); }
    setTesting(null); load();
  };
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  return (
    <div className="mx-auto max-w-[1400px]" data-testid="webhook-console">
      <p className="overline text-[#F27A54]">Cross-Site Delivery</p>
      <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Webhook Console</h1>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
        Register sister-site endpoints to receive a signed <code className="rounded bg-zinc-100 px-1 py-0.5">photo.published</code> event
        the moment a photo is approved and publicly reusable. Each delivery is HMAC-SHA256 signed
        (header <code className="rounded bg-zinc-100 px-1 py-0.5">X-PhotoPulse-Signature</code>).
      </p>

      {/* Register */}
      <Card className="mt-6 rounded-md border border-border p-5 shadow-none">
        <div className="flex items-center gap-2"><Plus size={18} weight="bold" className="text-[#F27A54]" /><h3 className="font-display font-bold">Register a webhook</h3></div>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="min-w-[320px] flex-1">
            <label className="overline text-[10px]">Endpoint URL (HTTPS)</label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://naturepulse.net/hooks/photopulse"
              className="mt-1 h-9 rounded-md" data-testid="webhook-url-input" />
          </div>
          <div>
            <label className="overline text-[10px]">Sister site (optional)</label>
            <select value={site} onChange={(e) => setSite(e.target.value)}
              className="mt-1 h-9 w-52 rounded-md border border-input bg-background px-3 text-sm" data-testid="webhook-site-select">
              <option value="">— none —</option>
              {SISTER_SITES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <Button onClick={create} disabled={creating} className="h-9 gap-1 rounded-md bg-[#F27A54] hover:bg-[#d9633f]" data-testid="webhook-create-btn">
            <Plus size={15} weight="bold" />{creating ? "Registering…" : "Register"}
          </Button>
        </div>
        {newSecret && (
          <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="webhook-secret-box">
            <p className="text-xs font-semibold text-amber-800">Signing secret for {newSecret.url} — copy it now, it is not shown again:</p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-white px-2 py-1 font-mono-p text-xs">{newSecret.secret}</code>
              <Button size="sm" variant="outline" className="h-8 gap-1 rounded-md" onClick={() => copy(newSecret.secret)}><Copy size={13} />Copy</Button>
              <Button size="sm" variant="ghost" className="h-8 rounded-md" onClick={() => setNewSecret(null)}>Dismiss</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Registered webhooks */}
      <Card className="mt-4 rounded-md border border-border p-5 shadow-none">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Broadcast size={18} weight="bold" className="text-[#F27A54]" /><h3 className="font-display font-bold">Registered endpoints</h3></div>
          <Button size="sm" variant="outline" className="h-8 gap-1 rounded-md" onClick={load} data-testid="webhook-refresh"><ArrowClockwise size={13} />Refresh</Button>
        </div>
        <div className="mt-4 space-y-2">
          {hooks.length === 0 && <p className="text-sm text-muted-foreground" data-testid="webhook-empty">No webhooks registered yet. Add one above.</p>}
          {hooks.map((h) => (
            <div key={h.id} className={`flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2.5 ${h.active ? "border-border" : "border-dashed border-zinc-300 opacity-60"}`} data-testid={`webhook-row-${h.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-sm font-semibold">{h.url}</p>
                  {h.site && <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-600">{h.site}</span>}
                  {!h.active && <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-[11px] font-medium text-zinc-600">disabled</span>}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {(h.events || []).join(", ")} · {h.delivery_count || 0} sent{h.fail_count ? ` · ${h.fail_count} failed` : ""}
                  {h.last_delivery_at ? ` · last ${new Date(h.last_delivery_at).toLocaleString()}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <StatusPill ok={h.last_ok} code={h.last_status_code} />
                <Button size="sm" variant="outline" className="h-8 gap-1 rounded-md" disabled={testing === h.id || !h.active} onClick={() => test(h.id)} data-testid={`webhook-test-${h.id}`}>
                  <Lightning size={13} weight="fill" />{testing === h.id ? "…" : "Test"}
                </Button>
                {h.active
                  ? <Button size="sm" variant="ghost" className="h-8 rounded-md text-red-600" onClick={() => remove(h.id)} data-testid={`webhook-disable-${h.id}`}><Trash size={14} /></Button>
                  : <Button size="sm" variant="ghost" className="h-8 rounded-md text-emerald-600" onClick={() => toggle(h.id, true)} data-testid={`webhook-enable-${h.id}`}>Enable</Button>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Recent deliveries */}
      <Card className="mt-4 rounded-md border border-border p-5 shadow-none">
        <div className="flex items-center gap-2"><Clock size={18} weight="bold" className="text-[#F27A54]" /><h3 className="font-display font-bold">Recent deliveries</h3></div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-2 pr-3 font-medium">When</th>
                <th className="py-2 pr-3 font-medium">Event</th>
                <th className="py-2 pr-3 font-medium">Endpoint</th>
                <th className="py-2 pr-3 font-medium">Result</th>
                <th className="py-2 pr-3 font-medium">Photo</th>
              </tr>
            </thead>
            <tbody data-testid="webhook-deliveries">
              {deliveries.length === 0 && <tr><td colSpan={5} className="py-4 text-muted-foreground">No deliveries recorded yet. Register a webhook and publish a photo (or hit Test).</td></tr>}
              {deliveries.map((d) => (
                <tr key={d.id} className="border-b border-border/60">
                  <td className="py-2 pr-3 whitespace-nowrap text-zinc-600">{d.at ? new Date(d.at).toLocaleString() : "—"}</td>
                  <td className="py-2 pr-3 font-mono-p">{d.event}</td>
                  <td className="py-2 pr-3 max-w-[280px] truncate">{d.url}</td>
                  <td className="py-2 pr-3"><StatusPill ok={d.ok} code={d.status_code} />{d.error && <span className="ml-2 text-red-500">{d.error}</span>}</td>
                  <td className="py-2 pr-3 font-mono-p text-zinc-500">{d.photo_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
